import { curLang } from './i18n';
import { t as tr } from './i18n';
import type { SiteTermId } from './siteTerms';

/**
 * Piața firmei: țara, moneda și limba ei. Le alege la înscriere și stau pe
 * firmă, ca Procumi să nu mai presupună România. Deocamdată interfața e doar în
 * română și sumele se arată în lei; câmpurile sunt temelia pentru pasul următor.
 *
 * Lista e scurtă și ținută de mână: fiecare țară trebuie să aibă o monedă
 * sigură, iar `Intl` nu dă moneda după țară.
 */

export interface Market {
  /** ISO 3166-1 alpha-2 */
  country: string;
  /** ISO 4217 */
  currency: string;
  /** limba documentelor și a mailurilor, când va exista mai mult de una */
  locale: string;
}

/** Țară → monedă. Ordinea nu contează: lista se sortează după nume la afișare. */
const CURRENCY: Record<string, string> = {
  RO: 'RON',
  MD: 'MDL',
  BG: 'BGN',
  HU: 'HUF',
  PL: 'PLN',
  CZ: 'CZK',
  RS: 'RSD',
  UA: 'UAH',
  TR: 'TRY',
  GB: 'GBP',
  CH: 'CHF',
  NO: 'NOK',
  SE: 'SEK',
  DK: 'DKK',
  IS: 'ISK',
  AL: 'ALL',
  BA: 'BAM',
  MK: 'MKD',
  // zona euro
  AT: 'EUR',
  BE: 'EUR',
  HR: 'EUR',
  CY: 'EUR',
  EE: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  GR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  ES: 'EUR',
  ME: 'EUR',
  XK: 'EUR',
  // în afara Europei
  US: 'USD',
  CA: 'CAD',
  MX: 'MXN',
  BR: 'BRL',
  AU: 'AUD',
  NZ: 'NZD',
  AE: 'AED',
  SA: 'SAR',
  QA: 'QAR',
  IL: 'ILS',
  EG: 'EGP',
  MA: 'MAD',
  ZA: 'ZAR',
  NG: 'NGN',
  KE: 'KES',
  IN: 'INR',
  SG: 'SGD',
  JP: 'JPY',
};

export const COUNTRIES = Object.keys(CURRENCY);

/**
 * Fusul orar al firmei, după țară: ora la care pleacă mailul de dimineață.
 * Țările cu mai multe fusuri (SUA, Canada, Mexic, Brazilia, Australia) primesc
 * fusul cel mai populat, până când firma își poate alege singură.
 */
const TIME_ZONE: Record<string, string> = {
  RO: 'Europe/Bucharest',
  MD: 'Europe/Chisinau',
  BG: 'Europe/Sofia',
  HU: 'Europe/Budapest',
  PL: 'Europe/Warsaw',
  CZ: 'Europe/Prague',
  RS: 'Europe/Belgrade',
  UA: 'Europe/Kyiv',
  TR: 'Europe/Istanbul',
  GB: 'Europe/London',
  CH: 'Europe/Zurich',
  NO: 'Europe/Oslo',
  SE: 'Europe/Stockholm',
  DK: 'Europe/Copenhagen',
  IS: 'Atlantic/Reykjavik',
  AL: 'Europe/Tirane',
  BA: 'Europe/Sarajevo',
  MK: 'Europe/Skopje',
  AT: 'Europe/Vienna',
  BE: 'Europe/Brussels',
  HR: 'Europe/Zagreb',
  CY: 'Asia/Nicosia',
  EE: 'Europe/Tallinn',
  FI: 'Europe/Helsinki',
  FR: 'Europe/Paris',
  DE: 'Europe/Berlin',
  GR: 'Europe/Athens',
  IE: 'Europe/Dublin',
  IT: 'Europe/Rome',
  LV: 'Europe/Riga',
  LT: 'Europe/Vilnius',
  LU: 'Europe/Luxembourg',
  MT: 'Europe/Malta',
  NL: 'Europe/Amsterdam',
  PT: 'Europe/Lisbon',
  SK: 'Europe/Bratislava',
  SI: 'Europe/Ljubljana',
  ES: 'Europe/Madrid',
  ME: 'Europe/Podgorica',
  XK: 'Europe/Belgrade',
  US: 'America/New_York',
  CA: 'America/Toronto',
  MX: 'America/Mexico_City',
  BR: 'America/Sao_Paulo',
  AU: 'Australia/Sydney',
  NZ: 'Pacific/Auckland',
  AE: 'Asia/Dubai',
  SA: 'Asia/Riyadh',
  QA: 'Asia/Qatar',
  IL: 'Asia/Jerusalem',
  EG: 'Africa/Cairo',
  MA: 'Africa/Casablanca',
  ZA: 'Africa/Johannesburg',
  NG: 'Africa/Lagos',
  KE: 'Africa/Nairobi',
  IN: 'Asia/Kolkata',
  SG: 'Asia/Singapore',
  JP: 'Asia/Tokyo',
};

export const timeZoneOf = (country: string): string => TIME_ZONE[country] ?? 'Europe/Bucharest';

/** Limba: română unde se vorbește, altfel engleză. */
const ROMANIAN = new Set(['RO', 'MD']);

export const DEFAULT_COUNTRY = 'RO';

export const isCountry = (code: string): boolean => code in CURRENCY;

export function marketFor(country: string): Market {
  const c = isCountry(country) ? country : DEFAULT_COUNTRY;
  return { country: c, currency: CURRENCY[c]!, locale: ROMANIAN.has(c) ? 'ro' : 'en' };
}

/** Numele țării în română („Germania”); codul, dacă motorul nu știe numele. */
export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([curLang()], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Țările pentru o listă de alegere: România întâi, restul după nume. */
export function countryOptions(): { code: string; name: string }[] {
  const rest = COUNTRIES.filter((c) => c !== DEFAULT_COUNTRY)
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, curLang()));
  return [{ code: DEFAULT_COUNTRY, name: countryName(DEFAULT_COUNTRY) }, ...rest];
}

/**
 * Cum se cheamă datele legale în țara firmei. În România: CUI (cu cifra de
 * control verificată) și Reg. Com. În rest: codul fiscal / de TVA, fără
 * verificare — formatele diferă de la țară la țară.
 */
export function legalLabels(country: string): { taxId: string; reg: string; checkCui: boolean } {
  return country === 'RO'
    ? { taxId: 'CUI', reg: tr('Nr. Reg. Com.'), checkCui: true }
    : { taxId: tr('Cod fiscal (VAT)'), reg: tr('Nr. înregistrare'), checkCui: false };
}

/*
 * Întrebările de la înscriere. Răspunsurile stau pe firmă: activitatea alege
 * categoriile propuse în agenda de furnizori, iar amândouă îți spun ție cine
 * se înscrie.
 */

export const ACTIVITIES = [
  {
    id: 'general',
    get label() {
      return tr('Construcții generale (antreprenor)');
    },
    group: 'build',
    siteTerm: 'santier',
  },
  {
    id: 'structure',
    get label() {
      return tr('Structuri și beton armat');
    },
    group: 'build',
    siteTerm: 'santier',
  },
  {
    id: 'mep',
    get label() {
      return tr('Instalații (HVAC, sanitare, electrice)');
    },
    group: 'build',
    siteTerm: 'santier',
  },
  {
    id: 'finishes',
    get label() {
      return tr('Finisaje și amenajări');
    },
    group: 'build',
    siteTerm: 'santier',
  },
  {
    id: 'infra',
    get label() {
      return tr('Drumuri și infrastructură');
    },
    group: 'build',
    siteTerm: 'santier',
  },
  {
    id: 'manufacturing',
    get label() {
      return tr('Producție și industrie');
    },
    group: 'other',
    siteTerm: 'punct',
  },
  {
    id: 'trade',
    get label() {
      return tr('Comerț și distribuție');
    },
    group: 'other',
    siteTerm: 'locatie',
  },
  {
    id: 'horeca',
    get label() {
      return tr('Hoteluri, restaurante, catering');
    },
    group: 'other',
    siteTerm: 'locatie',
  },
  {
    id: 'facility',
    get label() {
      return tr('Facility management și servicii');
    },
    group: 'other',
    siteTerm: 'proiect',
  },
  {
    id: 'logistics',
    get label() {
      return tr('Transport și logistică');
    },
    group: 'other',
    siteTerm: 'punct',
  },
  {
    id: 'agri',
    get label() {
      return tr('Agricultură și zootehnie');
    },
    group: 'other',
    siteTerm: 'ferma',
  },
  {
    id: 'health',
    get label() {
      return tr('Clinici și laboratoare');
    },
    group: 'other',
    siteTerm: 'locatie',
  },
  {
    id: 'office',
    get label() {
      return tr('Birouri, IT și servicii profesionale');
    },
    group: 'other',
    siteTerm: 'proiect',
  },
  {
    id: 'other',
    get label() {
      return tr('Altceva');
    },
    group: 'other',
    siteTerm: 'locatie',
  },
] as const;

/** Grupurile din lista de la înscriere: construcțiile întâi, de unde a pornit Procumi. */
export const ACTIVITY_GROUPS = [
  {
    id: 'build',
    get label() {
      return tr('Construcții');
    },
  },
  {
    id: 'other',
    get label() {
      return tr('Alte domenii');
    },
  },
] as const;

export type Activity = (typeof ACTIVITIES)[number]['id'];

export const isActivity = (v: string): v is Activity => ACTIVITIES.some((a) => a.id === v);

export const activityLabel = (v: string): string => ACTIVITIES.find((a) => a.id === v)?.label ?? '';

/**
 * Cum îi spune firma locului unde ajunge marfa, după ce face (siteTerms.ts).
 * Fără răspuns: șantier, ca firmele de dinainte, toate din construcții.
 */
export const siteTermForActivity = (v: string): SiteTermId =>
  ACTIVITIES.find((a) => a.id === v)?.siteTerm ?? 'santier';

/**
 * Firma din construcții: are antemăsurătoarea, chestionarul de cofraje și
 * chiria pe zile. Celelalte domenii nu le văd. Fără răspuns la înscriere:
 * construcții, ca toate firmele de dinainte.
 */
export const isBuildActivity = (v: string | null | undefined): boolean =>
  !v || ACTIVITIES.find((a) => a.id === v)?.group !== 'other';

export const BUYERS = [
  {
    id: '1',
    get label() {
      return tr('Doar eu');
    },
  },
  {
    id: '2-5',
    get label() {
      return tr('2–5 oameni');
    },
  },
  {
    id: '6+',
    get label() {
      return tr('6 sau mai mulți');
    },
  },
] as const;

export type Buyers = (typeof BUYERS)[number]['id'];

export const isBuyers = (v: string): v is Buyers => BUYERS.some((b) => b.id === v);

export const buyersLabel = (v: string): string => BUYERS.find((b) => b.id === v)?.label ?? '';

/**
 * Cotele de TVA ale țării, în procente: întâi cea standard, apoi cele reduse (după
 * ele se verifică TVA-ul scris într-o ofertă). România păstrează și cotele de
 * dinainte de august 2025, pentru ofertele vechi. Țară necunoscută: 20%.
 */
const VAT: Record<string, number[]> = {
  RO: [21, 19, 11, 9, 5],
  MD: [20, 8],
  BG: [20, 9],
  HU: [27, 18, 5],
  PL: [23, 8, 5],
  CZ: [21, 12],
  SK: [23, 19, 5],
  SI: [22, 9.5, 5],
  HR: [25, 13, 5],
  RS: [20, 10],
  UA: [20, 7],
  TR: [20, 10, 1],
  GB: [20, 5],
  IE: [23, 13.5, 9],
  DE: [19, 7],
  AT: [20, 13, 10],
  CH: [8.1, 2.6],
  FR: [20, 10, 5.5],
  BE: [21, 12, 6],
  NL: [21, 9],
  LU: [17, 14, 8],
  IT: [22, 10, 5, 4],
  ES: [21, 10, 4],
  PT: [23, 13, 6],
  GR: [24, 13, 6],
  CY: [19, 9, 5],
  MT: [18, 7, 5],
  DK: [25],
  SE: [25, 12, 6],
  NO: [25, 15, 12],
  FI: [25.5, 14, 10],
  EE: [22, 9],
  LV: [21, 12, 5],
  LT: [21, 9, 5],
};

export const vatRatesOf = (country: string): number[] => VAT[country] ?? [20];

/** Cota standard de TVA a țării, în procente: pusă implicit pe comenzi. */
export const vatRateOf = (country: string): number => vatRatesOf(country)[0]!;
